// Pure billing-validation extracted from PrescriptionDetailsPage.validFormForBilling.
// No Vue, no axios. The getter becomes a thin adapter over this function, and a
// transition watcher (not this function) merges the two field classes.
//
// The super-admin composition for future-written-date blocking lives HERE, once:
// callers pass the RAW config flag in `blockFutureWrittenDates`.

import moment from 'moment'
import { ValidatorGroup } from '@/util/ValidatorGroup'
import { RefillStatusCode } from '@/models/Prescription'
import { isWrittenDateBlocked } from './dateRules'

export interface BillingValidationPrescription {
    storeID: number
    patientID: number
    packageID: number
    prescriberID: number
    supervisingPhysicianId: number | null | undefined
    programID: number
    daySupply: number
    quantity: number
    dispensed: number
    originCode: number | string
    refills: number
    remainingQuantity: number
    rfNumber: number
    fillDate: string | Date | null | undefined
    effectiveDate: string | Date | null | undefined
    directions: string | null | undefined
}

export interface BillingValidationInput {
    prescription: BillingValidationPrescription
    enableSupervisingPhysician: boolean
    doctorRequiresSupervisingPhysician: boolean
    /** Top-level numeric origin code (distinct from prescription.originCode). */
    originCode: number
    writtenDate: string | Date | null | undefined
    expiration: string | Date | null | undefined
    initialDispensedQty: number
    dispensedQuantity: number
    statusCode: number
    /** RAW config flag; super-admin composition happens inside this function. */
    blockFutureWrittenDates: boolean
    hasSuperAdminPermission: boolean
    enforceEscriptEffectiveDate: boolean
    allowNewRefill: boolean
    newRefillMode: boolean
    packageSizeMultipleInvalid: boolean
    requireDirectionsAtEntry: boolean
    /** Pre-composed isNPIInvalid. */
    npiInvalid: boolean
    isDrugActive: boolean
    isHubMode: boolean
    requireNextTouchDateOnHold: boolean
    nextTouchDateManualOverride: boolean
    nextTouchDateLocal: string | Date | null | undefined
    now: Date
}

export interface BillingValidationResult {
    isValid: boolean
    /** Only the two keys the getter owns today. dispense is failure-only. */
    fieldClasses: { dispense: string; writtenDate: string }
    fieldsPending: string
}

export function validateForBilling(input: BillingValidationInput): BillingValidationResult {
    const p = input.prescription

    const valGrpEntities = new ValidatorGroup([
        new ValidatorGroup.Validator('Store', !!p.storeID),
        new ValidatorGroup.Validator('Patient', !!p.patientID),
        new ValidatorGroup.Validator('Drug', !!p.packageID),
        new ValidatorGroup.Validator('Doctor', !!p.prescriberID),
        new ValidatorGroup.Validator(
            'Supervising Physician',
            !input.enableSupervisingPhysician
                || !input.doctorRequiresSupervisingPhysician
                || !!p.supervisingPhysicianId,
        ),
        new ValidatorGroup.Validator('Program', !!p.programID),
    ])
    // evaluate() is typed boolean|undefined; all validator values here are
    // booleans, so !! is a behavior-preserving coercion that also fixes the type.
    const entitiesWereSelected = !!valGrpEntities.evaluate()

    const valGrpQuantities = new ValidatorGroup([
        new ValidatorGroup.Validator('Day Supply', p.daySupply?.toString() != '' && p.daySupply >= 0),
        new ValidatorGroup.Validator('Quantity', p.quantity?.toString() != '' && p.quantity >= 0),
        new ValidatorGroup.Validator('Dispensed', p.dispensed?.toString() != '' && p.dispensed >= 0),
        new ValidatorGroup.Validator('Origin', p.originCode?.toString() != '' && input.originCode > 0),
        new ValidatorGroup.Validator('Refills', p.refills?.toString() != '' && p.refills >= 0),
    ])
    const quantitiesWereIntroduced = !!valGrpQuantities.evaluate()

    // Super-admin composition of the future-written-date block happens here, once.
    const shouldBlockFuture = input.blockFutureWrittenDates && !input.hasSuperAdminPermission
    const writtenDateNotInFuture = input.writtenDate
        ? !isWrittenDateBlocked({
            writtenDate: input.writtenDate,
            now: input.now,
            blockFutureWrittenDates: shouldBlockFuture,
        })
        : true

    const valGrpDates = new ValidatorGroup([
        new ValidatorGroup.Validator('Written Date', !!input.writtenDate),
        new ValidatorGroup.Validator('Expiration', !!input.expiration),
        new ValidatorGroup.Validator('Future Written Date', writtenDateNotInFuture),
    ])
    const datesWereIntroduced = !!valGrpDates.evaluate()

    const remainingQuantity =
        p.remainingQuantity + (p.rfNumber == 0 ? 0 : input.initialDispensedQty)

    let valDispense = input.dispensedQuantity <= remainingQuantity
    if (valDispense) {
        if (input.statusCode < 3 && input.dispensedQuantity == 0) {
            valDispense = false
        }
    }

    const fillDateBeforeExpiration = moment(p.fillDate).isBefore(input.expiration)
    const fillDateNotBeforeWrittenDate = !p.fillDate || !input.writtenDate ||
        moment(p.fillDate).isSameOrAfter(moment(input.writtenDate), 'day')
    const fillDateNotBeforeEffectiveDate = !input.enforceEscriptEffectiveDate ||
        !p.fillDate || !p.effectiveDate ||
        input.hasSuperAdminPermission ||
        moment(p.fillDate).isSameOrAfter(moment(p.effectiveDate), 'day')
    const validRefillMode = (input.allowNewRefill && input.newRefillMode) || !input.newRefillMode
    const packageSizeMultipleValid = !input.packageSizeMultipleInvalid
    const directionsValid = !input.requireDirectionsAtEntry || !!p.directions?.trim()

    const valGrpLongNames = new ValidatorGroup([
        new ValidatorGroup.Validator('Dispensed quantity must be less than total allowed to dispense', input.dispensedQuantity <= remainingQuantity),
        new ValidatorGroup.Validator('Dispensed quantity must be an even multiple of the package size', packageSizeMultipleValid),
        new ValidatorGroup.Validator('Fill date should be before expiration date', fillDateBeforeExpiration),
        new ValidatorGroup.Validator('Fill date cannot be before the written date', fillDateNotBeforeWrittenDate),
        new ValidatorGroup.Validator('Fill date cannot be before the effective date', fillDateNotBeforeEffectiveDate),
        new ValidatorGroup.Validator('Mismatch in Prescriber NPI', !input.npiInvalid),
        new ValidatorGroup.Validator('Not Refillable', validRefillMode),
        new ValidatorGroup.Validator('Drug is not active', !!input.isDrugActive || input.isHubMode),
        new ValidatorGroup.Validator('Directions (Sig)', directionsValid),
    ])

    const allGroups = new ValidatorGroup([
        ...valGrpQuantities.validators,
        ...valGrpDates.validators,
        ...valGrpEntities.validators,
        ...valGrpLongNames.validators,
    ])
    const fieldsPending = allGroups.concatValidatorNames(false)

    // Field classes the getter owns: dispense (failure-only; zero-dispense is a
    // warning that still passes) and writtenDate (else-cleared).
    let dispenseClass = ''
    if (!valDispense && quantitiesWereIntroduced) {
        if (input.dispensedQuantity === 0) {
            dispenseClass = 'has-warning'
            valDispense = true
        } else {
            dispenseClass = 'has-danger'
        }
    }
    if (!packageSizeMultipleValid) {
        dispenseClass = 'has-danger'
    }
    const writtenDateClass = !writtenDateNotInFuture ? 'has-danger' : ''

    let nextTouchDateValid = true
    if (input.statusCode == RefillStatusCode.OnHold
        && (input.requireNextTouchDateOnHold || input.nextTouchDateManualOverride)) {
        if (!input.nextTouchDateLocal) {
            nextTouchDateValid = false
        } else {
            const ntd = moment(input.nextTouchDateLocal)
            nextTouchDateValid = ntd.isValid() && ntd.isAfter(moment(input.now))
        }
    }

    const isValidRaw =
        entitiesWereSelected &&
        quantitiesWereIntroduced &&
        datesWereIntroduced &&
        valDispense &&
        packageSizeMultipleValid &&
        fillDateBeforeExpiration &&
        fillDateNotBeforeWrittenDate &&
        fillDateNotBeforeEffectiveDate &&
        validRefillMode &&
        !input.npiInvalid &&
        (input.isDrugActive || input.isHubMode) &&
        nextTouchDateValid &&
        directionsValid

    return {
        isValid: !!isValidRaw,
        fieldClasses: { dispense: dispenseClass, writtenDate: writtenDateClass },
        fieldsPending,
    }
}
